import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { fuzzySearch } from "utils/fuzzySearch";
import useLocalTableSort from "views/Account/Integrations/registry/shared/hooks/useLocalTableSort";

import { FILTERS_ORDER_SETTINGS_KEY_VERSION_INPUTS } from "./constants";
import {
  MODULE_VERSION_OPTIONAL_INPUTS_TABLE_CELLS,
  MODULE_VERSION_REQUIRED_INPUTS_TABLE_CELLS,
} from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  MODULE_VERSION_OPTIONAL_INPUTS_TABLE_FILTERS_STORAGE_KEY,
  MODULE_VERSION_REQUIRED_INPUTS_TABLE_FILTERS_STORAGE_KEY,
  OPTIONAL_INPUTS_INITIAL_SORT_OPTION,
  OPTIONAL_INPUTS_SORT_CONFIG,
  REQUIRED_INPUTS_INITIAL_SORT_OPTION,
  REQUIRED_INPUTS_SORT_CONFIG,
} from "./Table/constants";
import { ModuleVersionOptionalInputRow, ModuleVersionRequiredInputRow } from "./Table/types";
import useModuleVersionOptionalInputsTableStore from "./Table/useOptionalTableStore";
import useModuleVersionRequiredInputsTableStore from "./Table/useRequiredTableStore";
import ModuleVersionCollapsibleSection from "../components/CollapsibleSection";
import sectionStyles from "../components/CollapsibleSection/styles.module.css";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import { sortModuleVersionRowsById } from "../helpers";

type ModuleVersionInputsProps = {
  inputs: Array<{
    name: string;
    description: string;
    required: boolean;
    type: string;
    default: string;
  }>;
};

const ModuleVersionInputs = ({ inputs }: ModuleVersionInputsProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);
  const trackAnalytics = useAnalytics();

  const {
    externalSortManagement: requiredExternalSortManagement,
    filterParams: requiredFilterParams,
  } = useLocalTableSort({
    localStorageKey: MODULE_VERSION_REQUIRED_INPUTS_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: REQUIRED_INPUTS_SORT_CONFIG,
    initialSortOption: REQUIRED_INPUTS_INITIAL_SORT_OPTION,
    initialSortDirection: INITIAL_SORT_DIRECTION,
  });

  const {
    externalSortManagement: optionalExternalSortManagement,
    filterParams: optionalFilterParams,
  } = useLocalTableSort({
    localStorageKey: MODULE_VERSION_OPTIONAL_INPUTS_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: OPTIONAL_INPUTS_SORT_CONFIG,
    initialSortOption: OPTIONAL_INPUTS_INITIAL_SORT_OPTION,
    initialSortDirection: INITIAL_SORT_DIRECTION,
  });

  const [filteredRequiredInputs, filteredOptionalInputs] = useMemo(() => {
    const rows = inputs.map((input) => ({ ...input, id: input.name }));

    const filtered = searchQuery
      ? fuzzySearch(rows, searchQuery.trim(), {
          keys: ["id", "description"],
          scoreThreshold: -1000,
        })
      : rows;

    const required: ModuleVersionRequiredInputRow[] = [];
    const optional: ModuleVersionOptionalInputRow[] = [];

    for (const input of filtered) {
      if (input.required) {
        required.push({ id: input.id, description: input.description, type: input.type });
      } else {
        optional.push({
          id: input.id,
          description: input.description,
          type: input.type,
          default: input.default,
        });
      }
    }

    return [required, optional];
  }, [inputs, searchQuery]);

  const sortedRequiredInputs = useMemo(
    () => sortModuleVersionRowsById(filteredRequiredInputs, requiredFilterParams),
    [filteredRequiredInputs, requiredFilterParams]
  );

  const sortedOptionalInputs = useMemo(
    () => sortModuleVersionRowsById(filteredOptionalInputs, optionalFilterParams),
    [filteredOptionalInputs, optionalFilterParams]
  );

  const isEmpty = sortedRequiredInputs.length === 0 && sortedOptionalInputs.length === 0;

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Inputs"
          tooltip="Required and optional inputs, their type, and default values."
        />

        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.INPUTS}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_INPUTS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper gap="x-large">
        {isEmpty && (
          <EmptyState
            fullHeight
            icon={EmptystateSearchNoResultsColored}
            caption={
              searchQuery
                ? `No inputs match the search query`
                : `This module has no inputs variables`
            }
            announce={!!searchQuery}
          />
        )}
        {sortedRequiredInputs.length > 0 && (
          <ModuleVersionCollapsibleSection
            title="Required inputs"
            count={sortedRequiredInputs.length}
          >
            <TableSimple
              data={sortedRequiredInputs}
              store={useModuleVersionRequiredInputsTableStore}
              wrapperClassName={sectionStyles.tableWrapper}
              ariaLabel="Required inputs"
              trackAnalytics={trackAnalytics}
              cellPerColumn={MODULE_VERSION_REQUIRED_INPUTS_TABLE_CELLS}
              filtersStorageKey={MODULE_VERSION_REQUIRED_INPUTS_TABLE_FILTERS_STORAGE_KEY}
              sortConfig={REQUIRED_INPUTS_SORT_CONFIG}
              externalSortManagement={requiredExternalSortManagement}
              hasPagination={false}
            />
          </ModuleVersionCollapsibleSection>
        )}
        {sortedOptionalInputs.length > 0 && (
          <ModuleVersionCollapsibleSection
            title="Optional inputs"
            count={sortedOptionalInputs.length}
          >
            <TableSimple
              data={sortedOptionalInputs}
              store={useModuleVersionOptionalInputsTableStore}
              wrapperClassName={sectionStyles.tableWrapper}
              ariaLabel="Optional inputs"
              trackAnalytics={trackAnalytics}
              cellPerColumn={MODULE_VERSION_OPTIONAL_INPUTS_TABLE_CELLS}
              filtersStorageKey={MODULE_VERSION_OPTIONAL_INPUTS_TABLE_FILTERS_STORAGE_KEY}
              sortConfig={OPTIONAL_INPUTS_SORT_CONFIG}
              externalSortManagement={optionalExternalSortManagement}
              hasPagination={false}
            />
          </ModuleVersionCollapsibleSection>
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionInputs;
